import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { sessionAPI, practiceAPI, vocabAPI } from '../../api/client'
import InteractiveTranscript from '../../components/InteractiveTranscript'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

const STEP_LABELS: Record<string, string> = {
  quick_diagnostic: 'Kiểm tra nhanh',
  real_content: 'Nội dung thực',
  micro_drill: 'Luyện tập',
  vocab_review: 'Ôn từ vựng',
}

const SKILL_META: Record<string, { label: string; path: string; desc: string; icon: string; color: string }> = {
  listening: { label: 'Luyện Nghe', path: '/listening', desc: 'Nghe audio thực từ BBC/TED, trả lời câu hỏi hiểu bài', icon: 'headphones', color: '#006571' },
  reading:   { label: 'Luyện Đọc',  path: '/reading',  desc: '3 Parts đọc VSTEP, gap-fill và MCQ', icon: 'menu_book', color: '#004be2' },
  writing:   { label: 'Luyện Viết', path: '/writing',  desc: 'Task 1 & 2 VSTEP, AI chấm điểm phản hồi chi tiết', icon: 'edit_note', color: '#8e00d5' },
  speaking:  { label: 'Luyện Nói',  path: '/speaking', desc: 'Ghi âm, AI phân tích phát âm và nội dung', icon: 'mic', color: '#b41340' },
}

/** FR-004 — Daily Session Engine */
export default function SessionPage() {
  const navigate = useNavigate()
  const [session, setSession] = useState<any>(null)
  const [step, setStep] = useState(0)
  const [minutes, setMinutes] = useState(15)

  const start = useMutation({
    mutationFn: () => sessionAPI.start(minutes),
    onSuccess: (data: any) => {
      if (data?.needsDiagnostic) {
        navigate('/diagnostic')
        return
      }
      setSession(data)
    },
  })

  const complete = useMutation({
    mutationFn: () => sessionAPI.complete(session.sessionId),
  })

  // ── Start Screen ──────────────────────────────────────────────────────────
  if (!session) {
    return (
      <div style={{ minHeight: '100vh', background: '#f6f6ff', fontFamily: "'Manrope', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        {/* Blobs */}
        <div style={{ position: 'fixed', top: '-8rem', right: '-8rem', width: '28rem', height: '28rem', borderRadius: '50%', background: 'rgba(0,75,226,0.06)', filter: 'blur(80px)', pointerEvents: 'none' }} />
        <div style={{ position: 'fixed', bottom: '-8rem', left: '-8rem', width: '28rem', height: '28rem', borderRadius: '50%', background: 'rgba(142,0,213,0.05)', filter: 'blur(80px)', pointerEvents: 'none' }} />

        <div style={{ background: 'white', borderRadius: '2rem', padding: '3rem', maxWidth: '28rem', width: '100%', boxShadow: '0 20px 60px rgba(0,75,226,0.08)', textAlign: 'center', position: 'relative', paddingLeft: 'clamp(1.5rem, 5vw, 3rem)', paddingRight: 'clamp(1.5rem, 5vw, 3rem)' }}>
          {/* Close */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{ position: 'absolute', top: '1.25rem', right: '1.25rem', background: 'none', border: 'none', cursor: 'pointer', color: '#535b71', padding: '0.375rem' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '1.375rem' }}>close</span>
          </button>

          {/* Icon */}
          <div style={{ width: '4rem', height: '4rem', borderRadius: '1.25rem', background: 'linear-gradient(135deg, #004be2, #809bff)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', boxShadow: '0 8px 24px rgba(0,75,226,0.25)' }}>
            <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '1.875rem', fontVariationSettings: "'FILL' 1" }}>bolt</span>
          </div>

          <H style={{ display: 'block', fontSize: '1.875rem', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>Phiên học hôm nay</H>
          <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6, marginBottom: '2rem' }}>
            Bạn có bao nhiêu thời gian? AI sẽ tối ưu nội dung phù hợp.
          </p>

          {/* Time selector */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '2rem' }}>
            {[5, 10, 15, 30].map((m) => {
              const sel = minutes === m
              return (
                <button
                  key={m}
                  onClick={() => setMinutes(m)}
                  style={{
                    padding: '1rem', borderRadius: '1rem', border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                    background: sel ? 'linear-gradient(135deg, #004be2, #809bff)' : '#eef0ff',
                    color: sel ? 'white' : '#272e42',
                    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '1.0625rem',
                    boxShadow: sel ? '0 4px 16px rgba(0,75,226,0.3)' : 'none',
                    transform: sel ? 'scale(1.03)' : 'none',
                  }}
                >
                  {m} phút
                </button>
              )
            })}
          </div>

          {start.isError && (
            <div style={{ padding: '0.875rem', borderRadius: '0.75rem', background: 'rgba(180,19,64,0.08)', color: '#b41340', fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem' }}>
              Không thể bắt đầu phiên học. Thử lại nhé.
            </div>
          )}

          <button
            onClick={() => start.mutate()}
            disabled={start.isPending}
            style={{
              width: '100%', padding: '1.125rem', borderRadius: '9999px', border: 'none',
              cursor: start.isPending ? 'not-allowed' : 'pointer',
              background: start.isPending ? 'rgba(0,75,226,0.4)' : 'linear-gradient(135deg, #004be2, #809bff)',
              color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1.0625rem',
              boxShadow: '0 8px 24px rgba(0,75,226,0.25)', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.625rem',
            }}
          >
            {start.isPending ? (
              <><span className="spinner" style={{ width: '1.25rem', height: '1.25rem', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Đang chuẩn bị...</>
            ) : (
              <>Bắt đầu học ngay <span className="material-symbols-outlined" style={{ fontSize: '1.25rem', fontVariationSettings: "'FILL' 1" }}>play_arrow</span></>
            )}
          </button>
        </div>
      </div>
    )
  }

  const steps = session.config.steps as string[]
  const isDone = step >= steps.length
  const currentStep = steps[step]

  // ── Completion Screen ─────────────────────────────────────────────────────
  if (isDone) {
    const xpGained = complete.data?.xp ?? 0
    const xpPct = 75 // placeholder progress to next level

    return (
      <div style={{ minHeight: '100vh', background: '#f6f6ff', fontFamily: "'Manrope', sans-serif", position: 'relative', overflow: 'hidden' }}>
        {/* Ambient glow */}
        <div style={{ position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', height: '37.5rem', background: 'linear-gradient(to bottom, rgba(0,75,226,0.1), transparent)', filter: 'blur(7.5rem)', pointerEvents: 'none', zIndex: 0 }} />

        <main style={{ position: 'relative', zIndex: 1, maxWidth: '64rem', margin: '0 auto', padding: '3rem 1.5rem 6rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Hero */}
          <section style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem', marginBottom: '1.5rem', background: '#d1dcff', borderRadius: '9999px' }}>
              <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '2.5rem', fontVariationSettings: "'FILL' 1" }}>celebration</span>
            </div>
            <H style={{ display: 'block', fontSize: 'clamp(2.5rem, 7vw, 4.5rem)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 0.95, marginBottom: '1rem' }}>
              Session Complete!
            </H>
            <p style={{ color: '#535b71', fontSize: '1.125rem', fontWeight: 500, maxWidth: '28rem', margin: '0 auto' }}>
              Tuyệt vời! Bạn vừa nâng cấp kỹ năng ngôn ngữ thêm {minutes} phút nữa.
            </p>
          </section>

          {/* Bento grid */}
          <div className="bento-12col" style={{ width: '100%' }}>
            {/* XP Circle */}
            <div className="bento-col-7" style={{ background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(24px)', borderRadius: '1.5rem', padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.04)' }}>
              <div style={{ position: 'relative', width: '12rem', height: '12rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <svg width="192" height="192" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="96" cy="96" r="80" fill="none" stroke="#d1dcff" strokeWidth="12" />
                  <circle cx="96" cy="96" r="80" fill="none" stroke="#004be2" strokeWidth="12"
                    strokeDasharray={`${2 * Math.PI * 80}`}
                    strokeDashoffset={`${2 * Math.PI * 80 * (1 - xpPct / 100)}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.625rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(0,75,226,0.6)', marginBottom: '0.25rem' }}>Total XP</span>
                  <H style={{ fontSize: '2.5rem', fontWeight: 900, color: '#004be2', lineHeight: 1 }}>2,450</H>
                  {xpGained > 0 && <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#535b71' }}>+{xpGained} kiếm được</span>}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <H style={{ display: 'block', fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>Level 14: Fluency Pioneer</H>
                <span style={{ fontSize: '0.875rem', color: '#535b71' }}>350 XP đến Level 15</span>
              </div>
            </div>

            {/* Streak card */}
            <div className="bento-col-5" style={{ background: 'linear-gradient(135deg, #8e00d5, #7000aa)', borderRadius: '1.5rem', padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', color: 'white', boxShadow: '0 20px 40px rgba(142,0,213,0.2)' }}>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)', marginBottom: '1rem' }}>Daily Streak</p>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '6rem', display: 'block', fontVariationSettings: "'FILL' 1", color: 'rgba(255,255,255,0.9)' }}>local_fire_department</span>
                  <div style={{ position: 'absolute', bottom: '-0.5rem', left: '50%', transform: 'translateX(-50%)', background: 'white', color: '#8e00d5', borderRadius: '9999px', padding: '0.25rem 1rem', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 900, fontSize: '1.25rem', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                    14
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <H style={{ display: 'block', fontSize: '1.375rem', fontWeight: 900, color: 'white', marginBottom: '0.5rem' }}>Streak Updated!</H>
                <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.875rem' }}>Bạn thuộc top 5% học viên tháng này!</p>
              </div>
            </div>

            {/* Milestones */}
            {(complete.data?.newMilestones?.length ?? 0) > 0 && (
              <div className="bento-col-12" style={{ background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(24px)', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
                <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem', marginBottom: '1rem' }}>🏅 Thành tựu mới</H>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                  {(complete.data?.newMilestones ?? []).map((m: string) => (
                    <span key={m} style={{ padding: '0.5rem 1.25rem', borderRadius: '9999px', background: 'linear-gradient(135deg, #004be2, #809bff)', color: 'white', fontSize: '0.875rem', fontWeight: 700 }}>
                      {m.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="bento-col-12" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {!complete.data ? (
                <button
                  onClick={() => complete.mutate()}
                  disabled={complete.isPending}
                  style={{ padding: '1.125rem 3rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #004be2, #809bff)', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem', boxShadow: '0 8px 24px rgba(0,75,226,0.3)', display: 'flex', alignItems: 'center', gap: '0.625rem' }}
                >
                  {complete.isPending ? <><span className="spinner" style={{ width: '1rem', height: '1rem', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Đang lưu...</> : <><span className="material-symbols-outlined" style={{ fontSize: '1.25rem', fontVariationSettings: "'FILL' 1" }}>bolt</span> Nhận điểm XP</>}
                </button>
              ) : (
                <button
                  onClick={() => navigate('/progress')}
                  style={{ padding: '1.125rem 2.5rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #004be2, #809bff)', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem', boxShadow: '0 8px 24px rgba(0,75,226,0.3)', display: 'flex', alignItems: 'center', gap: '0.625rem' }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '1.25rem', fontVariationSettings: "'FILL' 1" }}>leaderboard</span>
                  Xem tiến độ
                </button>
              )}
              <button
                onClick={() => navigate('/dashboard')}
                style={{ padding: '1.125rem 2.5rem', borderRadius: '9999px', border: '2px solid #d1dcff', background: 'transparent', color: '#535b71', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}
              >
                Về trang chủ
              </button>
            </div>
          </div>
        </main>
      </div>
    )
  }

  // ── Active Session ────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#f6f6ff', fontFamily: "'Manrope', sans-serif", display: 'flex', flexDirection: 'column' }}>
      {/* Session Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(24px)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '1rem 2rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <H style={{ fontWeight: 900, fontSize: '1.25rem', letterSpacing: '-0.03em', color: '#004be2', fontStyle: 'italic' }}>EngHustle</H>
          <div style={{ width: '1px', height: '1.5rem', background: '#a5adc6' }} />
          <div>
            <span style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#004be2' }}>Phiên học hôm nay</span>
            <H style={{ fontSize: '1rem', fontWeight: 700 }}>{STEP_LABELS[currentStep] ?? currentStep}</H>
          </div>
        </div>

        {/* Neural node progress */}
        <div className="session-progress-nodes">
          {steps.map((s, i) => {
            const done = i < step
            const active = i === step
            return (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{
                  width: active ? '1rem' : '0.75rem',
                  height: active ? '1rem' : '0.75rem',
                  borderRadius: '50%',
                  background: done ? '#004be2' : active ? '#8e00d5' : '#d1dcff',
                  boxShadow: active ? '0 0 0 4px rgba(142,0,213,0.2), 0 0 12px rgba(142,0,213,0.4)' : done ? '0 0 0 4px rgba(0,75,226,0.15)' : 'none',
                  border: '2px solid white',
                  transition: 'all 0.3s',
                  flexShrink: 0,
                }} />
                {i < steps.length - 1 && (
                  <div style={{ width: '3rem', height: '0.1875rem', borderRadius: '9999px', background: done ? '#004be2' : '#d9e2ff' }} />
                )}
              </div>
            )
          })}
          <span style={{ marginLeft: '1rem', fontSize: '0.875rem', fontWeight: 700, color: '#535b71', fontFamily: "'Space Grotesk', sans-serif" }}>
            Bước {step + 1} / {steps.length}
          </span>
        </div>

        <button
          onClick={() => navigate('/dashboard')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#535b71', padding: '0.5rem', borderRadius: '50%' }}
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      </header>

      {/* Main content */}
      <main style={{ flex: 1, padding: '2rem', maxWidth: '72rem', margin: '0 auto', width: '100%' }}>
        <StepContent
          stepType={currentStep}
          skillFocus={session.skillFocus}
          sessionId={session.sessionId}
          drillErrorTypes={(session.program?.targetErrorTypes ?? []).map((w: any) => w.errorType)}
          onStepDone={() => setStep((s) => s + 1)}
        />
      </main>
    </div>
  )
}

// ─── Step Dispatcher ──────────────────────────────────────────────────────────

function StepContent({ stepType, skillFocus, sessionId, drillErrorTypes, onStepDone }: {
  stepType: string; skillFocus: string; sessionId: string; drillErrorTypes: string[]; onStepDone: () => void
}) {
  switch (stepType) {
    case 'quick_diagnostic':
      return <DrillStep skill={skillFocus} sessionId={sessionId} limit={3} label="Kiểm tra nhanh" onDone={onStepDone} />
    case 'micro_drill':
      // Targeted micro-drill (FR-006): focus on the learner's weak error types.
      return <DrillStep skill={skillFocus} sessionId={sessionId} limit={5} errorTypes={drillErrorTypes} label="Luyện tập cá nhân hoá" onDone={onStepDone} />
    case 'vocab_review':
      return <VocabReviewStep onDone={onStepDone} />
    case 'real_content':
      return <RealContentStep skillFocus={skillFocus} onDone={onStepDone} />
    default:
      return (
        <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <p style={{ color: '#535b71', marginBottom: '1.5rem' }}>{stepType}</p>
          <ContinueButton onClick={onStepDone} label="Tiếp tục →" />
        </div>
      )
  }
}

// ─── Shared components ────────────────────────────────────────────────────────

function ContinueButton({ onClick, label, disabled }: { onClick: () => void; label: string; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '1rem 2.5rem', borderRadius: '9999px', border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: disabled ? 'rgba(0,75,226,0.4)' : '#060e20',
        color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.9375rem',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)', transition: 'all 0.2s',
        display: 'inline-flex', alignItems: 'center', gap: '0.625rem',
      }}
    >
      {label}
      <span className="material-symbols-outlined" style={{ fontSize: '1.125rem' }}>arrow_forward</span>
    </button>
  )
}

// ─── DrillStep — MCQ practice ─────────────────────────────────────────────────

function DrillStep({ skill, sessionId, limit, label, errorTypes, onDone }: {
  skill: string; sessionId: string; limit: number; label: string; errorTypes?: string[]; onDone: () => void
}) {
  const [qIndex, setQIndex] = useState(0)
  const [selected, setSelected] = useState<string | null>(null)
  const [result, setResult] = useState<{ isCorrect: boolean | null; correctAnswer: string; explanation: string } | null>(null)
  const [score, setScore] = useState({ correct: 0, total: 0 })

  const targeted = (errorTypes?.length ?? 0) > 0
  const { data, isLoading, isError } = useQuery({
    queryKey: ['session-drill', skill, errorTypes ?? []],
    queryFn: () => (targeted ? practiceAPI.getDrill(skill, errorTypes!) : practiceAPI.getQuestions(skill)),
  })

  const submitAnswer = useMutation({
    mutationFn: ({ questionId, answer }: { questionId: string; answer: string }) =>
      practiceAPI.submitAnswer(questionId, answer, 30, sessionId),
    onSuccess: (res) => {
      setResult(res)
      setScore(s => ({ correct: s.correct + (res.isCorrect ? 1 : 0), total: s.total + 1 }))
    },
  })

  if (isLoading) return <LoadingCard />
  if (isError) return (
    <div style={{ textAlign: 'center', padding: '4rem' }}>
      <p style={{ color: '#535b71', marginBottom: '1.5rem' }}>Không tải được câu hỏi.</p>
      <ContinueButton onClick={onDone} label="Bỏ qua bước này" />
    </div>
  )

  const questions = (data?.questions ?? []).slice(0, limit)
  const aiAnswers: any[] = data?.aiAnswers ?? []

  if (questions.length === 0) return (
    <div style={{ textAlign: 'center', padding: '4rem' }}>
      <p style={{ color: '#535b71', marginBottom: '1.5rem' }}>Chưa có câu hỏi cho kỹ năng <strong>{skill}</strong>.</p>
      <ContinueButton onClick={onDone} label="Bỏ qua" />
    </div>
  )

  const allDone = qIndex >= questions.length
  if (allDone) return (
    <div style={{ maxWidth: '36rem', margin: '0 auto', textAlign: 'center', padding: '4rem 2rem' }}>
      <div style={{ width: '5rem', height: '5rem', borderRadius: '50%', background: 'linear-gradient(135deg, #004be2, #809bff)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', boxShadow: '0 8px 24px rgba(0,75,226,0.3)' }}>
        <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '2rem', fontVariationSettings: "'FILL' 1" }}>emoji_events</span>
      </div>
      <H style={{ display: 'block', fontSize: '1.75rem', fontWeight: 900, marginBottom: '0.5rem' }}>{label} — Kết quả</H>
      <p style={{ color: '#535b71', marginBottom: '2rem' }}>
        Đúng <span style={{ color: '#004be2', fontWeight: 800 }}>{score.correct}/{score.total}</span> câu
      </p>
      <ContinueButton onClick={onDone} label="Bước tiếp theo" />
    </div>
  )

  const q = questions[qIndex]
  const isAI = String(q.id).startsWith('ai_')
  const aiAnswer = isAI ? aiAnswers[qIndex] : null

  function handleSelect(opt: string) {
    if (result) return
    setSelected(opt)
    if (isAI) {
      setResult({ isCorrect: null, correctAnswer: aiAnswer?.correctAnswer ?? '', explanation: aiAnswer?.explanation ?? '' })
      setScore(s => ({ ...s, total: s.total + 1 }))
    } else {
      submitAnswer.mutate({ questionId: q.id, answer: opt })
    }
  }

  function next() { setSelected(null); setResult(null); setQIndex(i => i + 1) }

  return (
    <div className="drill-2col">
      {/* Left: question context */}
      <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '2.5rem', minHeight: '24rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#8e00d5', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>bolt</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8e00d5' }}>{label}</span>
        </div>
        <p style={{ color: '#535b71', fontSize: '0.875rem', lineHeight: 1.6 }}>
          Kỹ năng: <span style={{ color: '#004be2', fontWeight: 700, textTransform: 'capitalize' }}>{skill}</span>
          &nbsp;· Câu {qIndex + 1}/{questions.length}
        </p>

        {/* Audio player for listening skill */}
        {skill === 'listening' && data?.content?.url && (
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'white', borderRadius: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.75rem' }}>
              <span className="material-symbols-outlined" style={{ color: '#006571', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>headphones</span>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#006571' }}>
                {data.content.title ?? 'Audio'}
              </span>
            </div>
            <audio
              controls
              src={data.content.url}
              style={{ width: '100%', borderRadius: '0.5rem' }}
            >
              Trình duyệt không hỗ trợ audio.
            </audio>
          </div>
        )}

        {skill === 'listening' && !data?.content?.url && (
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(180,83,9,0.06)', borderRadius: '1rem', border: '1px solid rgba(180,83,9,0.2)' }}>
            <p style={{ color: '#b45309', fontSize: '0.8125rem', fontWeight: 600, margin: 0 }}>
              ⚠️ Chưa có audio cho bài này. Admin cần upload file MP3 trong trang Listening Content.
            </p>
          </div>
        )}
        {/* Progress bar */}
        <div style={{ width: '100%', height: '0.25rem', background: '#d1dcff', borderRadius: '9999px', marginTop: '1.5rem', overflow: 'hidden' }}>
          <div style={{ width: `${((qIndex) / questions.length) * 100}%`, height: '100%', background: 'linear-gradient(90deg, #004be2, #809bff)', borderRadius: '9999px', transition: 'width 0.4s' }} />
        </div>

        {/* Score indicator */}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '2rem', flexWrap: 'wrap' }}>
          {Array.from({ length: questions.length }).map((_, i) => (
            <div key={i} style={{
              width: '2rem', height: '2rem', borderRadius: '50%',
              background: i < qIndex ? '#004be2' : i === qIndex ? '#8e00d5' : '#d1dcff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: i <= qIndex ? 'white' : '#a5adc6',
              fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '0.75rem',
            }}>
              {i < qIndex ? <span className="material-symbols-outlined" style={{ fontSize: '0.875rem', fontVariationSettings: "'FILL' 1" }}>check</span> : i + 1}
            </div>
          ))}
        </div>
      </div>

      {/* Right: MCQ card */}
      <div style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(24px)', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 20px 40px rgba(0,0,0,0.06)' }}>
        <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 800, marginBottom: '1.5rem', lineHeight: 1.4 }}>
          {q.prompt}
        </H>

        {Array.isArray(q.options) && q.options.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {q.options.map((opt: string) => {
              // correctAnswer may be just a letter ("C") or full text ("C. Five")
              // opt is always full text ("C. Five") — match by leading letter
              const extractLetter = (s: string) => s.trim().match(/^([A-Da-d])[.\s]/)?.[1]?.toUpperCase() ?? s.trim().toUpperCase()
              const isCorrectOpt = result ? extractLetter(opt) === extractLetter(result.correctAnswer) : false
              const isSelectedOpt = opt === selected

              let bg = 'rgba(255,255,255,0.5)'
              let border = '1px solid rgba(165,173,198,0.3)'
              let color = '#272e42'
              if (result) {
                if (isCorrectOpt) { bg = 'rgba(0,75,226,0.08)'; border = '2px solid #004be2'; color = '#004be2' }
                else if (isSelectedOpt) { bg = 'rgba(180,19,64,0.06)'; border = '2px solid #b41340'; color = '#b41340' }
              } else if (isSelectedOpt) {
                bg = 'rgba(0,75,226,0.06)'; border = '2px solid rgba(0,75,226,0.4)'
              }
              return (
                <button
                  key={opt}
                  onClick={() => handleSelect(opt)}
                  disabled={!!result}
                  style={{
                    width: '100%', textAlign: 'left', padding: '1rem 1.25rem', borderRadius: '1rem',
                    border, background: bg, cursor: result ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
                    transition: 'all 0.2s', color, fontWeight: 600, fontSize: '0.9375rem',
                  }}
                >
                  <span>{opt}</span>
                  <div style={{
                    width: '1.25rem', height: '1.25rem', borderRadius: '50%', flexShrink: 0,
                    border: isCorrectOpt ? 'none' : '2px solid #a5adc6',
                    background: isCorrectOpt ? '#004be2' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isCorrectOpt && (
                      <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '0.875rem', fontVariationSettings: "'FILL' 1" }}>check</span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        ) : (
          !result && (
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <input
                type="text"
                placeholder="Nhập câu trả lời..."
                value={selected ?? ''}
                onChange={e => setSelected(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && selected && handleSelect(selected)}
                style={{ flex: 1, padding: '0.875rem 1rem', borderRadius: '0.75rem', background: '#eef0ff', border: 'none', outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem' }}
              />
              <button
                onClick={() => selected && handleSelect(selected)}
                disabled={!selected}
                style={{ padding: '0.875rem 1.5rem', borderRadius: '0.75rem', border: 'none', background: '#004be2', color: 'white', fontWeight: 700, cursor: selected ? 'pointer' : 'not-allowed' }}
              >
                Trả lời
              </button>
            </div>
          )
        )}

        {result && (
          <div style={{
            padding: '1rem', borderRadius: '1rem', marginBottom: '1.5rem',
            background: result.isCorrect === false ? 'rgba(180,19,64,0.06)' : result.isCorrect ? 'rgba(0,75,226,0.06)' : '#eef0ff',
          }}>
            {result.isCorrect === true && <p style={{ color: '#004be2', fontWeight: 700, marginBottom: '0.375rem' }}>✓ Chính xác!</p>}
            {result.isCorrect === false && <p style={{ color: '#b41340', fontWeight: 700, marginBottom: '0.375rem' }}>✗ Đáp án đúng: <strong>{result.correctAnswer}</strong></p>}
            {result.isCorrect === null && <p style={{ color: '#535b71', fontWeight: 700, marginBottom: '0.375rem' }}>Đáp án: <strong>{result.correctAnswer}</strong></p>}
            {result.explanation && <p style={{ color: '#535b71', fontSize: '0.875rem', lineHeight: 1.6 }}>{result.explanation}</p>}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={onDone}
            style={{ flex: 1, padding: '0.875rem', borderRadius: '9999px', border: 'none', background: '#d1dcff', color: '#535b71', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}
          >
            Bỏ qua
          </button>
          {result ? (
            <button
              onClick={next}
              style={{ flex: 2, padding: '0.875rem', borderRadius: '9999px', border: 'none', background: '#060e20', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
            >
              {qIndex + 1 < questions.length ? 'Câu tiếp' : 'Xem kết quả'}
              <span className="material-symbols-outlined" style={{ fontSize: '1.125rem' }}>arrow_forward</span>
            </button>
          ) : (
            <button
              onClick={() => selected && handleSelect(selected)}
              disabled={!selected}
              style={{ flex: 2, padding: '0.875rem', borderRadius: '9999px', border: 'none', background: selected ? 'linear-gradient(135deg, #004be2, #809bff)' : '#d1dcff', color: selected ? 'white' : '#a5adc6', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.875rem', cursor: selected ? 'pointer' : 'not-allowed', transition: 'all 0.2s' }}
            >
              Xác nhận
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── VocabReviewStep ──────────────────────────────────────────────────────────

function VocabReviewStep({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient()
  const { data: dueWords = [], isLoading } = useQuery({
    queryKey: ['vocab-due'],
    queryFn: vocabAPI.due,
  })

  const [idx, setIdx] = useState(0)
  const [showAnswer, setShowAnswer] = useState(false)

  const review = useMutation({
    mutationFn: ({ wordId, quality }: { wordId: string; quality: number }) => vocabAPI.review(wordId, quality),
    onSuccess: () => {
      setShowAnswer(false)
      const words = (dueWords as any[]).slice(0, 5)
      if (idx + 1 >= words.length) {
        qc.invalidateQueries({ queryKey: ['vocab-due'] })
        onDone()
      } else {
        setIdx(i => i + 1)
      }
    },
  })

  if (isLoading) return <LoadingCard />

  const words = (dueWords as any[]).slice(0, 5)

  if (words.length === 0) return (
    <div style={{ textAlign: 'center', padding: '4rem' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
      <H style={{ display: 'block', fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.5rem' }}>Chưa có từ vựng để ôn!</H>
      <p style={{ color: '#535b71', marginBottom: '0.75rem' }}>
        Bạn chưa lưu từ vựng nào. Hãy click vào từ trong phần Transcript khi luyện nghe để lưu từ vào bộ thẻ.
      </p>
      <p style={{ color: '#a5adc6', fontSize: '0.8125rem', marginBottom: '2rem' }}>
        Từ đã lưu sẽ xuất hiện để ôn vào ngày hôm sau theo hệ thống SM-2.
      </p>
      <ContinueButton onClick={onDone} label="Bỏ qua, tiếp tục" />
    </div>
  )

  if (idx >= words.length) return (
    <div style={{ textAlign: 'center', padding: '4rem' }}>
      <H style={{ display: 'block', fontSize: '1.75rem', fontWeight: 900, marginBottom: '0.5rem' }}>Ôn từ vựng xong!</H>
      <p style={{ color: '#535b71', marginBottom: '2rem' }}>Đã ôn {words.length} từ hôm nay.</p>
      <ContinueButton onClick={onDone} label="Bước tiếp theo" />
    </div>
  )

  const word = words[idx]

  return (
    <div style={{ maxWidth: '40rem', margin: '0 auto' }}>
      {/* Progress */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#535b71' }}>Ôn từ vựng</span>
        <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, color: '#004be2' }}>{idx + 1} / {words.length}</span>
      </div>
      <div style={{ width: '100%', height: '0.25rem', background: '#d1dcff', borderRadius: '9999px', marginBottom: '2rem', overflow: 'hidden' }}>
        <div style={{ width: `${(idx / words.length) * 100}%`, height: '100%', background: 'linear-gradient(90deg, #004be2, #809bff)', borderRadius: '9999px', transition: 'width 0.4s' }} />
      </div>

      {/* Flashcard */}
      <div style={{ background: 'white', borderRadius: '2rem', padding: '3rem', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,75,226,0.08)', minHeight: '14rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#535b71', fontSize: '1.125rem', lineHeight: 1.7, marginBottom: '2rem' }}>
          {word.contextSentence?.replace(word.word, '________') ?? '________'}
        </p>

        {!showAnswer ? (
          <button
            onClick={() => setShowAnswer(true)}
            style={{ padding: '0.875rem 2rem', borderRadius: '9999px', border: '2px solid #004be2', background: 'transparent', color: '#004be2', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer' }}
          >
            Xem đáp án
          </button>
        ) : (
          <div style={{ width: '100%' }}>
            <H style={{ display: 'block', fontSize: '2rem', fontWeight: 900, color: '#004be2', marginBottom: '0.5rem' }}>{word.word}</H>
            <p style={{ color: '#535b71', marginBottom: '2rem' }}>Bạn nhớ từ này như thế nào?</p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {[
                { quality: 1, label: 'Không nhớ', color: '#b41340', bg: 'rgba(180,19,64,0.08)' },
                { quality: 3, label: 'Khó nhớ', color: '#b45309', bg: 'rgba(180,83,9,0.08)' },
                { quality: 5, label: 'Nhớ rõ', color: '#004be2', bg: 'rgba(0,75,226,0.08)' },
              ].map(opt => (
                <button
                  key={opt.quality}
                  onClick={() => review.mutate({ wordId: word.id, quality: opt.quality })}
                  disabled={review.isPending}
                  style={{
                    padding: '0.75rem 1.5rem', borderRadius: '9999px', border: 'none',
                    background: opt.bg, color: opt.color,
                    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── RealContentStep ──────────────────────────────────────────────────────────

function RealContentStep({ skillFocus, onDone }: { skillFocus: string; onDone: () => void }) {
  const navigate = useNavigate()
  const meta = SKILL_META[skillFocus] ?? SKILL_META.reading
  const audioRef = useRef<HTMLAudioElement>(null)
  const [playCount, setPlayCount] = useState(0)
  const [showTranscript, setShowTranscript] = useState(false)

  // For listening: load a content item inline so user can read transcript + save words
  const { data, isLoading } = useQuery({
    queryKey: ['session-real-content', skillFocus],
    queryFn: () => practiceAPI.getQuestions(skillFocus),
    enabled: skillFocus === 'listening',
  })

  const content = data?.content

  // Non-listening skills: redirect to skill page
  if (skillFocus !== 'listening') {
    return (
      <div style={{ maxWidth: '36rem', margin: '0 auto', textAlign: 'center' }}>
        <div style={{
          width: '5rem', height: '5rem', borderRadius: '1.5rem',
          background: `${meta.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1.5rem',
        }}>
          <span className="material-symbols-outlined" style={{ color: meta.color, fontSize: '2.5rem', fontVariationSettings: "'FILL' 1" }}>{meta.icon}</span>
        </div>
        <H style={{ display: 'block', fontSize: '1.75rem', fontWeight: 900, marginBottom: '0.75rem' }}>Nội dung thực</H>
        <H style={{ display: 'block', fontSize: '1.25rem', fontWeight: 700, color: meta.color, marginBottom: '0.75rem' }}>{meta.label}</H>
        <p style={{ color: '#535b71', lineHeight: 1.7, marginBottom: '2.5rem' }}>{meta.desc}</p>
        <p style={{ color: '#a5adc6', fontSize: '0.875rem', marginBottom: '2.5rem' }}>
          Hoàn thành bài luyện rồi quay lại đây để tiếp tục phiên học.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button
            onClick={() => navigate(meta.path)}
            style={{
              padding: '1.125rem 2.5rem', borderRadius: '9999px', border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg, #004be2, #809bff)', color: 'white',
              fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem',
              boxShadow: '0 8px 24px rgba(0,75,226,0.25)',
            }}
          >
            Vào trang {meta.label} →
          </button>
          <button
            onClick={onDone}
            style={{ padding: '1rem', borderRadius: '9999px', border: '2px solid #d1dcff', background: 'transparent', color: '#535b71', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.9375rem', cursor: 'pointer' }}
          >
            Đã xong, tiếp tục phiên học ✓
          </button>
        </div>
      </div>
    )
  }

  // Listening: inline content with audio player + interactive transcript
  if (isLoading) return <LoadingCard />

  if (!content?.url) {
    return (
      <div style={{ maxWidth: '36rem', margin: '0 auto', textAlign: 'center', padding: '3rem' }}>
        <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#a5adc6', display: 'block', marginBottom: '1rem' }}>pending</span>
        <H style={{ display: 'block', fontWeight: 700, color: '#535b71', marginBottom: '0.5rem' }}>Chưa có nội dung nghe</H>
        <p style={{ color: '#a5adc6', fontSize: '0.875rem', marginBottom: '2rem' }}>Admin cần publish nội dung trước.</p>
        <ContinueButton onClick={onDone} label="Bỏ qua bước này" />
      </div>
    )
  }

  return (
    <div style={{ maxWidth: '52rem', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div style={{ width: '2.75rem', height: '2.75rem', borderRadius: '0.875rem', background: '#e6fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span className="material-symbols-outlined" style={{ color: '#006571', fontSize: '1.375rem', fontVariationSettings: "'FILL' 1" }}>headphones</span>
        </div>
        <div>
          <H style={{ display: 'block', fontWeight: 800, fontSize: '1.0625rem' }}>{content.title ?? 'Bài nghe'}</H>
          <span style={{ fontSize: '0.75rem', color: '#006571', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {content.source ?? 'VSTEP Listening'}
          </span>
        </div>
        <div style={{ marginLeft: 'auto', padding: '0.375rem 0.875rem', borderRadius: '9999px', background: playCount >= 2 ? 'rgba(180,19,64,0.1)' : '#eef0ff', fontSize: '0.8125rem', fontWeight: 700, color: playCount >= 2 ? '#b41340' : '#004be2' }}>
          {playCount}/2 lần nghe
        </div>
      </div>

      {/* Audio player */}
      <div style={{ background: '#060e20', borderRadius: '1.25rem', padding: '1.25rem', marginBottom: '1.5rem' }}>
        <audio
          ref={audioRef}
          src={content.url}
          controls
          onPlay={() => {
            const audio = audioRef.current
            if (!audio) return
            if (audio.currentTime < 1) {
              if (playCount >= 2) { audio.pause(); audio.currentTime = 0; return }
              setPlayCount(c => c + 1)
            } else if (playCount >= 2) {
              audio.pause()
            }
          }}
          style={{ width: '100%', borderRadius: '0.75rem', opacity: playCount >= 2 ? 0.5 : 1, pointerEvents: playCount >= 2 ? 'none' : 'auto' }}
        />
        {playCount >= 2 && (
          <p style={{ marginTop: '0.625rem', fontSize: '0.8125rem', color: '#f74b6d', fontWeight: 600 }}>
            ⚠ Đã hết lượt nghe (tối đa 2 lần, đúng quy định thi).
          </p>
        )}
      </div>

      {/* Transcript toggle */}
      {content.transcript && (
        <div style={{ background: 'white', borderRadius: '1.25rem', overflow: 'hidden', marginBottom: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
          <button
            onClick={() => setShowTranscript(s => !s)}
            style={{ width: '100%', padding: '1rem 1.5rem', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.9375rem', color: '#272e42' }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '1.125rem', color: '#006571' }}>subtitles</span>
              Transcript + Interactive
            </span>
            <span className="material-symbols-outlined" style={{ color: '#a5adc6', transition: 'transform 0.2s', transform: showTranscript ? 'rotate(180deg)' : 'none' }}>expand_more</span>
          </button>
          {showTranscript && (
            <div style={{ padding: '0 1.5rem 1.5rem', borderTop: '1px solid #eef0ff' }}>
              <InteractiveTranscript
                text={content.transcript}
                contentId={content.id ?? ''}
                audioRef={audioRef}
                audioDuration={content.durationSeconds ?? undefined}
              />
            </div>
          )}
        </div>
      )}

      {/* Continue button */}
      <ContinueButton onClick={onDone} label="Tiếp tục phiên học →" />
    </div>
  )
}

// ─── Loading ──────────────────────────────────────────────────────────────────

function LoadingCard() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '20rem' }}>
      <div className="spinner" />
    </div>
  )
}
